import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  Chip,
  Pagination,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Link as LinkIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { apiService } from '../services/apiService';

const AdminPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [totalUsers, setTotalUsers] = useState(0);
  const [page, setPage] = useState(1);
  const [editDialog, setEditDialog] = useState({ open: false, user: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });

  // Registration links state
  const [registrationLinks, setRegistrationLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(true);
  const [deleteLinkDialog, setDeleteLinkDialog] = useState({ open: false, link: null });

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    is_admin: false
  });

  const usersPerPage = 10;

  useEffect(() => {
    loadUsers();
    loadRegistrationLinks();
  }, [page]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await apiService.getUsers(page, usersPerPage);
      setUsers(response.users || []);
      setTotalUsers(response.total || 0);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setFormData({
      email: user.email,
      password: '',
      is_admin: user.is_admin
    });
    setEditDialog({ open: true, user });
  };

  const handleDeleteUser = (user) => {
    setDeleteDialog({ open: true, user });
  };

  const handleSaveUser = async () => {
    try {
      const updates = {};
      if (formData.email !== editDialog.user.email) {
        updates.email = formData.email;
      }
      if (formData.password) {
        updates.password = formData.password;
      }
      if (formData.is_admin !== editDialog.user.is_admin) {
        updates.is_admin = formData.is_admin;
      }

      await apiService.updateUser(editDialog.user.id, updates);
      setSuccess('User updated successfully');
      setEditDialog({ open: false, user: null });
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await apiService.deleteUser(deleteDialog.user.id);
      setSuccess('User deleted successfully');
      setDeleteDialog({ open: false, user: null });
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleCloseDialogs = () => {
    setEditDialog({ open: false, user: null });
    setDeleteDialog({ open: false, user: null });
    setDeleteLinkDialog({ open: false, link: null });
    setFormData({ email: '', password: '', is_admin: false });
  };

  // Registration links functions
  const loadRegistrationLinks = async () => {
    try {
      setLinksLoading(true);
      const response = await apiService.getRegistrationLinks();
      setRegistrationLinks(response.links || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load registration links');
    } finally {
      setLinksLoading(false);
    }
  };

  const handleCreateRegistrationLink = async () => {
    try {
      const response = await apiService.createRegistrationLink();
      setSuccess(`Registration link created! URL: ${response.url}`);
      loadRegistrationLinks();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create registration link');
    }
  };

  const handleDeleteRegistrationLink = (link) => {
    setDeleteLinkDialog({ open: true, link });
  };

  const handleConfirmDeleteLink = async () => {
    try {
      await apiService.deleteRegistrationLink(deleteLinkDialog.link.id);
      setSuccess('Registration link deleted successfully');
      setDeleteLinkDialog({ open: false, link: null });
      loadRegistrationLinks();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete registration link');
    }
  };

  const handleCopyLink = async (link) => {
    const baseURL = window.location.origin;
    const fullURL = `${baseURL}/register?token=${link.token}`;
    try {
      await navigator.clipboard.writeText(fullURL);
      setSuccess('Registration link copied to clipboard');
    } catch (err) {
      setError('Failed to copy link to clipboard');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          User Administration
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Paper sx={{ width: '100%', overflow: 'hidden', mt: 3 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Admin</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.id}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        {user.is_admin ? (
                          <Chip label="Admin" color="primary" size="small" />
                        ) : (
                          <Chip label="User" color="default" size="small" />
                        )}
                      </TableCell>
                      <TableCell>{formatDate(user.created_at)}</TableCell>
                      <TableCell>{formatDate(user.updated_at)}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleEditUser(user)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteUser(user)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalUsers > usersPerPage && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <Pagination
                count={Math.ceil(totalUsers / usersPerPage)}
                page={page}
                onChange={(event, newPage) => setPage(newPage)}
                color="primary"
              />
            </Box>
          )}
        </Paper>

        {/* Registration Links Section */}
        <Typography variant="h5" gutterBottom sx={{ mt: 6 }}>
          Registration Links Management
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<LinkIcon />}
            onClick={handleCreateRegistrationLink}
          >
            Create Registration Link
          </Button>
        </Box>

        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Token</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Used By</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Used</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {linksLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : registrationLinks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No registration links found
                    </TableCell>
                  </TableRow>
                ) : (
                  registrationLinks.map((link) => (
                    <TableRow key={link.id}>
                      <TableCell>{link.id}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {link.token.substring(0, 16)}...
                        </Typography>
                      </TableCell>
                      <TableCell>{link.created_by_user?.email || 'Unknown'}</TableCell>
                      <TableCell>{link.used_by_user?.email || '-'}</TableCell>
                      <TableCell>
                        {link.used_by ? (
                          <Chip label="Used" color="success" size="small" />
                        ) : (
                          <Chip label="Available" color="primary" size="small" />
                        )}
                      </TableCell>
                      <TableCell>{formatDate(link.created_at)}</TableCell>
                      <TableCell>{link.used_at ? formatDate(link.used_at) : '-'}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleCopyLink(link)}
                          color="primary"
                          title="Copy registration link"
                        >
                          <CopyIcon />
                        </IconButton>
                        {!link.used_by && (
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteRegistrationLink(link)}
                            color="error"
                            title="Delete unused link"
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Edit User Dialog */}
        <Dialog open={editDialog.open} onClose={handleCloseDialogs} maxWidth="sm" fullWidth>
          <DialogTitle>Edit User</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <TextField
                autoFocus
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="New Password (leave blank to keep current)"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                margin="normal"
                helperText="Minimum 8 characters"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_admin}
                    onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                  />
                }
                label="Administrator privileges"
                sx={{ mt: 2 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialogs}>Cancel</Button>
            <Button onClick={handleSaveUser} variant="contained">Save</Button>
          </DialogActions>
        </Dialog>

        {/* Delete User Dialog */}
        <Dialog open={deleteDialog.open} onClose={handleCloseDialogs}>
          <DialogTitle>Delete User</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the user "{deleteDialog.user?.email}"?
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialogs}>Cancel</Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Registration Link Dialog */}
        <Dialog open={deleteLinkDialog.open} onClose={handleCloseDialogs}>
          <DialogTitle>Delete Registration Link</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this registration link?
              This action cannot be undone and the link will no longer work.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialogs}>Cancel</Button>
            <Button onClick={handleConfirmDeleteLink} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default AdminPage;